import React from 'react';
import {
  Type, Image as ImageIcon, Square, Circle, Triangle, Minus,
  ChevronUp, ChevronDown, Eye, EyeOff, Trash2,
  Lock, Unlock, Layers,
} from 'lucide-react';
import { CanvasElement } from '../types';
import './LayersPanel.css';

interface LayersPanelProps {
  elements: CanvasElement[];
  selectedId: string | null;
  onSelectId: (id: string) => void;
  onToggleVisible: (id: string) => void;
  onToggleLock: (id: string) => void;
  onDelete: (id: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRename: (id: string, name: string) => void;
}

const TYPE_ICON_MAP: Record<string, React.ReactNode> = {
  text:     <Type      size={11} strokeWidth={2} />,
  image:    <ImageIcon size={11} strokeWidth={2} />,
  rect:     <Square   size={11} strokeWidth={2} />,
  circle:   <Circle   size={11} strokeWidth={2} />,
  triangle: <Triangle size={11} strokeWidth={2} />,
  line:     <Minus    size={11} strokeWidth={2} />,
};

const LayersPanel: React.FC<LayersPanelProps> = ({
  elements, selectedId,
  onSelectId, onToggleVisible, onToggleLock, onDelete,
  onMoveUp, onMoveDown, onRename,
}) => {
  const reversed = [...elements].reverse();
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName,  setEditName]  = React.useState('');

  const startRename = (e: React.MouseEvent, el: CanvasElement) => {
    e.stopPropagation();
    setEditingId(el.id);
    setEditName(el.name);
  };

  const commitRename = () => {
    if (editingId && editName.trim()) onRename(editingId, editName.trim());
    setEditingId(null);
  };

  return (
    <div className="lp-root">
      <div className="lp-header">
        <span className="lp-header-label">Layers</span>
        {elements.length > 0 && (
          <span className="lp-count-badge">{elements.length}</span>
        )}
      </div>

      <div className="lp-scroll">
        {elements.length === 0 ? (
          <div className="lp-empty">
            <Layers size={28} strokeWidth={1.2} className="lp-empty-icon" />
            <span className="lp-empty-text">No layers yet.<br />Add text, images or shapes.</span>
          </div>
        ) : (
          reversed.map((el, reversedIndex) => {
            const originalIndex = elements.length - 1 - reversedIndex;
            const isSelected = el.id === selectedId;
            const isHidden   = !el.visible;
            const isLocked   = !!el.locked;
            const isEditing  = editingId === el.id;

            return (
              <div
                key={el.id}
                className={`lp-row${isSelected ? ' selected' : ''}${isHidden ? ' hidden-el' : ''}`}
                onClick={() => !isEditing && onSelectId(el.id)}
              >
                {/* Type badge */}
                <div className={`lp-type-icon ${el.type}`}>
                  {TYPE_ICON_MAP[el.type]}
                </div>

                {/* Name — double-click to rename */}
                {isEditing ? (
                  <input
                    className="lp-name-input"
                    value={editName}
                    autoFocus
                    onChange={e => setEditName(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={e => {
                      if (e.key === 'Enter')  commitRename();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span
                    className="lp-name"
                    title={`${el.name} — double-click to rename`}
                    onDoubleClick={e => startRename(e, el)}
                  >
                    {el.name}
                  </span>
                )}

                {/* Controls */}
                <div className="lp-controls">
                  <button
                    className="lp-ctrl-btn"
                    onClick={e => { e.stopPropagation(); onMoveUp(originalIndex); }}
                    disabled={originalIndex === elements.length - 1}
                    title="Move up"
                  ><ChevronUp size={11} strokeWidth={2.2} /></button>

                  <button
                    className="lp-ctrl-btn"
                    onClick={e => { e.stopPropagation(); onMoveDown(originalIndex); }}
                    disabled={originalIndex === 0}
                    title="Move down"
                  ><ChevronDown size={11} strokeWidth={2.2} /></button>

                  <button
                    className={`lp-ctrl-btn${isLocked ? ' is-locked' : ''}`}
                    onClick={e => { e.stopPropagation(); onToggleLock(el.id); }}
                    title={isLocked ? 'Unlock layer' : 'Lock layer'}
                  >{isLocked ? <Lock size={11} strokeWidth={2} /> : <Unlock size={11} strokeWidth={2} />}</button>

                  <button
                    className={`lp-ctrl-btn${isHidden ? ' is-hidden' : ''}`}
                    onClick={e => { e.stopPropagation(); onToggleVisible(el.id); }}
                    title={isHidden ? 'Show layer' : 'Hide layer'}
                  >{isHidden ? <EyeOff size={11} strokeWidth={2} /> : <Eye size={11} strokeWidth={2} />}</button>

                  <button
                    className="lp-ctrl-btn del"
                    onClick={e => { e.stopPropagation(); onDelete(el.id); }}
                    title="Delete layer"
                  ><Trash2 size={11} strokeWidth={2} /></button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LayersPanel;
